import React, { useCallback, useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { TextField, FormHelperText, Box, CircularProgress, Chip, Typography } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import debounce from 'lodash/debounce';

interface ServiceNameInputWithValidationProps extends FieldExtensionComponentProps<string> {
  formContext?: { formData?: Record<string, any> };
}

/**
 * ServiceNameInputWithValidation - 带 Component 名称唯一性校验的服务名输入框
 *
 * 功能：
 * - 用户输入服务名称
 * - 自动监听 environment 字段
 * - 实时检查组合后的 Component 名称（{服务名}-{环境}）是否已存在
 * - 如果已存在，显示错误并阻止提交
 *
 * 用法：
 * ```yaml
 * serviceName:
 *   title: 服务名称
 *   type: string
 *   ui:field: ServiceNameInputWithValidation
 *   ui:options:
 *     environmentField: environment
 * ```
 */
export const ServiceNameInputWithValidation = (
  props: ServiceNameInputWithValidationProps,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    uiSchema,
    idSchema,
    schema,
    formContext,
  } = props;

  const catalogApi = useApi(catalogApiRef);

  const [value, setValue] = useState(formData || '');
  const [checking, setChecking] = useState(false);
  const [existsError, setExistsError] = useState<string | null>(null);
  const [formatError, setFormatError] = useState<string | null>(null);
  const [componentName, setComponentName] = useState<string>('');

  const environmentField = uiSchema?.['ui:options']?.environmentField || 'environment';
  const environment = (formContext?.formData?.[environmentField] as string) || '';

  // 格式校验
  const validateFormat = (name: string): string | null => {
    if (!name) return null;
    if (!/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(name)) {
      return '名称必须以小写字母或数字开头，只能包含小写字母、数字和连字符';
    }
    if (name.length < 2) {
      return '名称至少需要 2 个字符';
    }
    if (name.length > 63) {
      return '名称不能超过 63 个字符';
    }
    return null;
  };

  // 检查 Component 是否已存在
  const checkComponentExists = useCallback(
    async (name: string): Promise<boolean> => {
      if (!name || name.length < 2) return false;

      try {
        const entityRef = `component:default/${name}`;
        const entity = await catalogApi.getEntityByRef(entityRef);
        return !!entity; // 如果返回实体，说明存在
      } catch {
        // getEntityByRef 在实体不存在时会抛出错误或返回 undefined
        return false;
      }
    },
    [catalogApi],
  );

  // 防抖检查 Component 名称
  const debouncedCheck = useCallback(
    debounce((serviceName: string, env: string) => {
      if (!serviceName || !env || validateFormat(serviceName)) {
        setExistsError(null);
        setChecking(false);
        setComponentName('');
        return;
      }

      const compName = `${serviceName}-${env}`;
      setComponentName(compName);

      setChecking(true);
      checkComponentExists(compName).then((exists) => {
        setChecking(false);
        if (exists) {
          setExistsError(`Component "${compName}" 已存在，请使用其他服务名称或环境`);
        } else {
          setExistsError(null);
        }
      });
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkComponentExists],
  );

  // 当 environment 变化时，重新检查
  useEffect(() => {
    if (value && environment) {
      debouncedCheck(value, environment);
    } else {
      setComponentName('');
      setExistsError(null);
      setChecking(false);
    }
  }, [value, environment, debouncedCheck]);

  // 处理输入变化
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.toLowerCase().trim();
    setValue(newValue);

    // 格式校验
    const fmtError = validateFormat(newValue);
    setFormatError(fmtError);

    // 如果格式正确，检查 Component 名称是否存在
    if (!fmtError && newValue && environment) {
      setChecking(true);
      debouncedCheck(newValue, environment);
    } else {
      setExistsError(null);
      setComponentName('');
    }

    // 只有格式正确时才更新表单值
    if (!fmtError) {
      onChange(newValue);
    } else {
      onChange(undefined);
    }
  };

  // 当存在性检查完成后，阻止提交
  useEffect(() => {
    if (existsError) {
      onChange(undefined); // 存在时清空值，阻止提交
    } else if (value && !formatError) {
      onChange(value);
    }
  }, [existsError, value, formatError, onChange]);

  const hasError = !!(formatError || existsError || (rawErrors && rawErrors.length > 0));
  const helperText = formatError || existsError || (checking ? '检查中...' : '');

  return (
    <>
      <TextField
        id={idSchema.$id}
        label={schema.title || '服务名称'}
        value={value}
        onChange={handleChange}
        required={required}
        error={hasError}
        helperText={helperText}
        fullWidth
        variant="outlined"
        placeholder="例如: hello-world"
        InputProps={{
          autoComplete: 'off',
        }}
      />
      {uiSchema?.['ui:help'] && !hasError && (
        <FormHelperText>{uiSchema['ui:help']}</FormHelperText>
      )}
      {componentName && (
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            组件名称: <strong>{componentName}</strong>
          </Typography>
          {checking && (
            <Box display="flex" alignItems="center" mt={0.5}>
              <CircularProgress size={14} style={{ marginRight: 6 }} />
              <Typography variant="caption" color="textSecondary">
                检查中...
              </Typography>
            </Box>
          )}
          {!checking && !existsError && componentName && (
            <Box mt={0.5}>
              <Chip
                label="Component 名称可用"
                size="small"
                style={{ backgroundColor: '#4caf50', color: 'white', fontSize: '0.75rem' }}
              />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

